import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Post } from "../../lib/posts";
import { formatDate } from "../../lib/date";
import { CategoryBadge } from "../taxonomy/CategoryBadge";
import { TagList } from "../taxonomy/TagList";
import { ArticleMeta } from "../posts/ArticleMeta";
import { TableOfContents } from "../posts/TableOfContents";
import { FeaturedImage } from "../posts/FeaturedImage";

type WikiArticleLayoutProps = {
  post: Post;
  children: ReactNode;
  related: ReactNode;
  comments: ReactNode;
};

export function WikiArticleLayout({ post, children, related, comments }: WikiArticleLayoutProps) {
  return (
    <article className="wiki-article">
      <header className="article-header">
        <div className="article-header__taxonomy">
          <CategoryBadge category={post.category} />
          <span>Updated {formatDate(post.updatedAt)}</span>
        </div>
        <h1>{post.title}</h1>
        <p className="article-excerpt">{post.excerpt}</p>
        <ArticleMeta post={post} />
      </header>

      <div className="article-grid">
        <div className="article-main">
          <figure className="featured-media">
            <FeaturedImage src={post.featuredImage} alt="" loading="eager" />
          </figure>
          {children}
          {related}
          {comments}
        </div>

        <aside className="article-aside" aria-label="Article details">
          <section className="article-infobox">
            <h2>Article info</h2>
            <dl>
              <div>
                <dt>Author</dt>
                <dd>
                  <Link to={post.authorUrl}>{post.authorProfile.name}</Link>
                </dd>
              </div>
              <div>
                <dt>Category</dt>
                <dd>
                  <Link to={post.categoryUrl}>{post.category}</Link>
                </dd>
              </div>
              <div>
                <dt>Published</dt>
                <dd>{formatDate(post.publishedAt)}</dd>
              </div>
            </dl>
            <TagList tags={post.tags} />
          </section>
          <TableOfContents items={post.toc} />
        </aside>
      </div>
    </article>
  );
}
